import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "../components/AuthLayout";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { safeAuthDestination } from "../lib/authDestination";
import PasswordInput from "../components/PasswordInput";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan")?.toLowerCase();
  const requestedNext = searchParams.get("next") || "";
  const next = safeAuthDestination(requestedNext);
  const selectedPlan = ["starter", "growth", "pro"].includes(requestedPlan) ? requestedPlan : "";
  const planLabel = selectedPlan ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1) : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const destination = next || (user?.role === "admin" ? "/admin/command-center" : selectedPlan ? `/checkout/${selectedPlan}` : "/dashboard");
  useEffect(() => { if (user) navigate(destination, { replace: true }); }, [user, navigate, destination]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(next || (u.role === "admin" ? "/admin/command-center" : selectedPlan ? `/checkout/${selectedPlan}` : "/dashboard"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

  return (
    <AuthLayout>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h1>
      <p className="mt-1 text-sm text-slate-500">Access your CRM data quality workspace.</p>
      {selectedPlan && <div data-testid="selected-plan-summary" className="mt-5 border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Continue with</div>
        <div className="mt-1 text-sm font-semibold text-slate-900">{planLabel} plan checkout</div>
      </div>}

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Email</label>
          <input data-testid="login-email-input" type="email" required value={email}
                 onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@company.com" />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Password</label>
          <PasswordInput data-testid="login-password-input" toggleTestId="login-password-toggle"
                         autoComplete="current-password" required value={password}
                         onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
        </div>
        <button data-testid="login-submit-button" type="submit" disabled={loading}
                className="w-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <div className="mt-3 text-right">
        <Link to="/forgot-password" data-testid="login-forgot-password-link"
              className="text-sm font-medium text-slate-700 underline underline-offset-2">Forgot password?</Link>
      </div>

      <p className="mt-6 text-sm text-slate-500">
        No account?{" "}
        <Link to={next ? `/register?next=${encodeURIComponent(next)}` : selectedPlan ? `/register?plan=${selectedPlan}` : "/register"} data-testid="go-to-register-link" className="font-medium text-slate-900 underline underline-offset-2">Create one</Link>
      </p>
    </AuthLayout>
  );
}
